import { QRCodeSVG } from "qrcode.react";
import { FC, MouseEvent, ReactNode } from "react";
import {
  Apr,
  Fiat,
  formatApr,
  getDenomIconSrc,
  useTranslation,
} from "rujira.ui";
import rujiraLogo from "rujira.ui/assets/images/rujira.png";
import {
  defaultStakingLendingBackground,
  stakingLendingBackgrounds,
} from "./backgrounds";
import { buildShareMessage, ShareCardButton } from "./ShareCardDialog";

const ASPECT_RATIO = 16 / 9;

export type StrategyCardProps = {
  copyUrl: string;
  assetLabel: string;
  apr: Apr | null | undefined;
  tvl: bigint;
};

type StrategyCardKind = "staking" | "lending";

const Stat: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="share-card__stat">
    <div className="fs-24 lh-16 color-grey fw-500">{label}</div>
    <div className="fs-28 lh-24 condensed fw-500 nowrap mt-1.5">{children}</div>
  </div>
);

const StrategyCard: FC<
  StrategyCardProps & { kind: StrategyCardKind; backgroundImage?: string }
> = ({
  copyUrl,
  assetLabel,
  apr,
  tvl,
  kind,
  backgroundImage = defaultStakingLendingBackground,
}) => {
  const { t } = useTranslation("common");
  const lending = kind === "lending";
  const tickerLength = assetLabel.trim().length;
  const tickerSizeClass =
    tickerLength <= 3
      ? "share-card__side--strategy-ticker-short"
      : "";
  const labels = lending
    ? {
        tag: t("shareLendingTag"),
        apr: t("shareLendingApr"),
        tvl: t("shareLendingTvl"),
        asset: t("shareLendingAsset"),
        callToAction: t("shareLendingCallToAction"),
      }
    : {
        tag: t("shareStakingTag"),
        apr: t("shareStakingApr"),
        tvl: t("shareStakingTvl"),
        asset: t("shareStakingAsset"),
        callToAction: t("shareStakingCallToAction"),
      };

  return (
    <div className="share-card">
      <img src={backgroundImage} alt="" className="share-card__background" />

      <div className="share-card__main flex dir-c">
        <div className="flex ai-c">
          <img
            src={rujiraLogo}
            alt="Rujira"
            className="share-card__logo share-card__logo--strategy"
          />
          <span
            className={`tag tag--lg ${lending ? "tag--teal" : "tag--primary"} ml-2`}>
            {labels.tag}
          </span>
        </div>

        <div className="my-a mt-14">
          <div className="fs-25 fw-500 lh-20 color-grey">{labels.apr}</div>
          <div className="share-card__apr fs-90 lh-76 fw-500 color-teal mt-2">
            {formatApr(apr)}
          </div>
        </div>

        <div className="share-card__stats flex pt-2">
          <Stat label={labels.tvl}>
            <Fiat amount={tvl} symbol="$" decimals={8} />
          </Stat>
        </div>
      </div>

      <div
        className={`share-card__side share-card__side--strategy ${tickerSizeClass} flex dir-c ml-3 pl-3`}>
        <div className="share-card__strategy-asset-label fs-25 fw-500 lh-20 color-grey">
          {labels.asset}
        </div>
        <div className="share-card__strategy-asset flex ai-c mt-1">
          <img
            src={getDenomIconSrc(assetLabel)}
            alt={assetLabel}
            className="icon-denom share-card__strategy-asset-icon"
          />
          <span className="share-card__strategy-asset-symbol ml-1 nowrap">
            {assetLabel}
          </span>
        </div>

        <div className="fs-16 lh-18 fw-600 color-purple text-center mt-a mb-0.5">
          {labels.callToAction}
        </div>
        <div className="share-card__qr mx-a mt-1.5">
          <QRCodeSVG value={copyUrl} size={128} level="M" />
        </div>
        <div className="fs-15 lh-16 text-center mt-1.5">{"rujira.network"}</div>
      </div>
    </div>
  );
};

const StrategyShareButton: FC<
  StrategyCardProps & { kind: StrategyCardKind }
> = ({ kind, ...card }) => {
  const { t } = useTranslation("common");
  const lending = kind === "lending";
  const labels = lending
    ? {
        title: t("shareLendingTitle"),
        cardTitle: t("shareLendingCardTitle", { asset: card.assetLabel }),
        message: t("shareLendingMessage", { asset: card.assetLabel }),
        prompt: t("shareLendingCopyPrompt", { asset: card.assetLabel }),
      }
    : {
        title: t("shareStakingTitle"),
        cardTitle: t("shareStakingCardTitle", { asset: card.assetLabel }),
        message: t("shareStakingMessage", { asset: card.assetLabel }),
        prompt: t("shareStakingCopyPrompt", { asset: card.assetLabel }),
      };

  return (
    <ShareCardButton
      className="ml-0.5"
      tooltip={t("share")}
      modalTitle={labels.title}
      shareTitle={labels.cardTitle}
      shareText={buildShareMessage({
        headline: labels.message,
        prompt: labels.prompt,
        url: card.copyUrl,
        tagline: t("shareTagline"),
      })}
      shareUrl={card.copyUrl}
      aspectRatio={ASPECT_RATIO}
      onClick={(event: MouseEvent<HTMLButtonElement>) =>
        event.stopPropagation()
      }>
      {stakingLendingBackgrounds.map(({ key, image }) => (
        <StrategyCard
          key={key}
          {...card}
          kind={kind}
          backgroundImage={image}
        />
      ))}
    </ShareCardButton>
  );
};

export const StakingShareButton: FC<StrategyCardProps> = (card) => (
  <StrategyShareButton {...card} kind="staking" />
);

export const LendingShareButton: FC<StrategyCardProps> = (card) => (
  <StrategyShareButton {...card} kind="lending" />
);
